import { SHOP_COLORS as S } from './invoiceEngine';
import { FitLine, FitZone } from './fitText';

/**
 * The shop-header pad — the other book the press prints: white stock washing
 * down to a yellow-green foot, the shop's name set large in green over a red
 * proprietor plate, a maroon address band, orange নাম / ঠিকানা tags, a blue
 * column head over a black grid, and the two signatures written straight on
 * the paper.
 *
 * Measured off a printed pad in A4 pixels and multiplied by `u` (the paper's
 * width against A4), so an A6 book is the same pad printed small. As on the
 * classic pad the header is a fixed block: whatever the shop writes in it is
 * set to fit rather than pushing the form down the sheet.
 */

/** The header block's height, as a share of the paper's width. */
const HEAD_RATIO = 0.3;

/** The words this pad prints that the other styles word differently. */
const SHOP_LABELS = {
    bn: { memoNo: 'ক্রমিক নং', inWords: 'কথায়', colon: 'ঃ' },
    en: { memoNo: 'SL No.', inWords: 'In words', colon: ':' },
};

/** An orange tag and the box written beside it — নাম, ঠিকানা. */
function TagField({ label, u }) {
    const height = 38 * u;
    return (
        <div style={{ display: 'flex', alignItems: 'stretch', height }}>
            {/* The word sits in a span of its own: the export lifts every
                line of type, and a box that is itself the line would carry its
                own background up with it. */}
            <div
                style={{
                    flex: 'none', minWidth: 76 * u, background: S.tag, color: '#FFFFFF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: `0 ${8 * u}px`, fontSize: 20 * u, fontWeight: 700, lineHeight: 1.2,
                    whiteSpace: 'nowrap', border: `${1.5 * u}px solid ${S.tag}`, boxSizing: 'border-box',
                }}
            >
                <span>{label}</span>
            </div>
            <div style={{ flex: 1, background: '#FFFFFF', border: `${1.5 * u}px solid ${S.tag}`, borderLeft: 'none' }} />
        </div>
    );
}

/** The ruled writing area: a blue head over an open black grid. */
function ShopGrid({ memo, u }) {
    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `${1.6 * u}px solid ${S.rule}`;
    const last = memo.columns.length - 1;

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <thead>
                <tr>
                    {memo.columns.map((col, i) => (
                        <th
                            key={col.id}
                            style={{
                                background: S.head,
                                color: '#FFFFFF',
                                height: 40 * u,
                                padding: `0 ${3 * u}px`,
                                fontSize: 21 * u,
                                fontWeight: 600,
                                lineHeight: 1.3,
                                textAlign: 'center',
                                border: `${1.5 * u}px solid ${S.rule}`,
                                // The head is split by hairlines of paper, as a
                                // press block is, not by the grid's own ink.
                                borderLeft: i === 0 ? `${1.5 * u}px solid ${S.rule}` : (memo.columnLines ? `${1.5 * u}px solid #FFFFFF` : 'none'),
                                borderRight: i === last ? `${1.5 * u}px solid ${S.rule}` : 'none',
                            }}
                        >
                            <span>{col.label || ' '}</span>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: memo.rows }, (_, i) => (
                    <tr key={i}>
                        {memo.columns.map((col, c) => (
                            <td
                                key={col.id}
                                style={{
                                    height: 30 * u,
                                    padding: 0,
                                    // The money column is washed with a cold tint,
                                    // the way the printed pad shades it.
                                    background: c === last ? `linear-gradient(90deg, rgba(216,225,238,0) 0%, ${S.money} 35%, #CFE6F7 70%, rgba(216,225,238,0.45) 100%)` : 'transparent',
                                    borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                    borderRight: (memo.columnLines || c === last) ? rule : 'none',
                                    borderBottom: memo.rowLines && i < memo.rows - 1 ? rule : 'none',
                                }}
                            />
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/** মোট / অগ্রিম / বাকী, ruled into the last two columns of the same grid. */
function ShopTotals({ memo, labels, u }) {
    const rows = [
        memo.totals.total ? labels.total : null,
        memo.totals.advance ? labels.advance : null,
        memo.totals.due ? labels.due : null,
    ].filter(Boolean);

    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `${1.6 * u}px solid ${S.rule}`;
    const span = Math.max(0, memo.columns.length - 2);

    if (!rows.length) {
        return <div style={{ borderTop: rule, borderLeft: rule, borderRight: rule, height: 0 }} />;
    }

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <tbody>
                {rows.map((label, i) => {
                    const lastRow = i === rows.length - 1;
                    return (
                        <tr key={label}>
                            {memo.columns.slice(0, span).map((col, c) => (
                                <td
                                    key={col.id}
                                    style={{
                                        borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                        borderRight: memo.columnLines && c === span - 1 ? rule : 'none',
                                        borderBottom: lastRow ? rule : 'none',
                                        height: 34 * u,
                                        padding: 0,
                                    }}
                                />
                            ))}
                            <td
                                style={{
                                    border: rule,
                                    borderBottom: lastRow ? rule : 'none',
                                    height: 34 * u,
                                    padding: `0 ${8 * u}px`,
                                    fontSize: 20 * u,
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    textAlign: 'right',
                                    color: S.ink,
                                }}
                            >
                                <span>{`${label}=`}</span>
                            </td>
                            <td
                                style={{
                                    border: rule,
                                    borderBottom: lastRow ? rule : 'none',
                                    height: 34 * u,
                                    padding: 0,
                                    background: `linear-gradient(90deg, rgba(216,225,238,0) 0%, ${S.money} 35%, #CFE6F7 70%, rgba(216,225,238,0.45) 100%)`,
                                }}
                            />
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

/* ------------------------------------------------------------------ *
 *  The sheet
 * ------------------------------------------------------------------ */

export default function ShopMemo({ memo, labels, title, accent, paperWidth, u }) {
    const { shop, show } = memo;
    const lang = memo.docLang === 'en' ? 'en' : 'bn';
    const words = SHOP_LABELS[lang];
    // A switch the saved pad has never heard of counts as on, as the form's
    // own chips read it.
    const on = (key) => show[key] !== false;
    const pad = 30 * u;
    const headHeight = Math.round(paperWidth * HEAD_RATIO);
    const headPad = { top: 10 * u, side: 30 * u, bottom: 6 * u };
    const phones = [shop.phone, shop.phone2].filter((n) => String(n || '').trim());
    const email = String(shop.email || '').trim();
    const contacts = [...phones, on('email') ? email : ''].filter(Boolean).join('  •  ');
    const addressLine = shop.address.split('\n').map((l) => l.trim()).filter(Boolean).join(', ');
    const bandLine = `${labels.address}${words.colon}${addressLine ? ` ${addressLine}` : ''}`;
    const headKey = [
        memo.bismillah, title, shop.name, memo.proprietor, memo.distributor, shop.tagline,
        shop.logo ? '1' : '0', contacts, JSON.stringify(show), u,
    ].join('~');

    return (
        <>
            {/* ---- the shop's own heading, a fixed block ---- */}
            <div style={{ position: 'relative', flex: 'none' }}>
                {/* The logo rides the corner of the block, so it costs the
                    heading no height of its own. */}
                {on('logo') && shop.logo ? (
                    <div
                        aria-hidden="true"
                        style={{
                            position: 'absolute', left: pad, top: 12 * u, zIndex: 1,
                            width: 120 * u, height: 56 * u,
                            backgroundImage: `url("${shop.logo}")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'left center',
                            backgroundSize: 'contain',
                        }}
                    />
                ) : null}

                <FitZone height={headHeight} align="centre" pad={headPad} signature={headKey}>
                    <div style={{ textAlign: 'center' }}>
                        {on('bismillah') && memo.bismillah.trim() ? (
                            <div style={{ fontSize: 13 * u, fontWeight: 600, lineHeight: 1.3, color: '#4A4A3A' }}>
                                {memo.bismillah}
                            </div>
                        ) : null}

                        {on('plate') ? (
                            <div
                                style={{
                                    display: 'inline-block', marginTop: 3 * u,
                                    background: '#FFFFFF', color: S.ink,
                                    border: `${1.5 * u}px solid ${S.ink}`, borderRadius: 999 * u,
                                    padding: `${2 * u}px ${16 * u}px`,
                                    fontSize: 18 * u, fontWeight: 700, lineHeight: 1.35,
                                }}
                            >
                                <span>{title}</span>
                            </div>
                        ) : null}

                        <FitLine
                            text={shop.name || ' '}
                            size={64 * u}
                            style={{
                                marginTop: 4 * u,
                                fontWeight: 800, lineHeight: 1.12, color: S.name,
                                textShadow: `${-1.2 * u}px ${-1.2 * u}px 0 #FFFFFF, ${1.2 * u}px ${-1.2 * u}px 0 #FFFFFF, ${-1.2 * u}px ${1.2 * u}px 0 #FFFFFF, ${1.2 * u}px ${1.2 * u}px 0 #FFFFFF, ${2 * u}px ${2.5 * u}px ${2 * u}px rgba(20,60,30,0.35)`,
                            }}
                        />

                        {/* The proprietor on his red plate, as the block is cut. */}
                        {on('proprietor') && memo.proprietor.trim() ? (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 5 * u }}>
                                <div
                                    style={{
                                        maxWidth: '82%',
                                        background: S.plate, color: '#FFFFFF',
                                        border: `${2 * u}px solid #E6E6E6`, borderRadius: 999 * u,
                                        padding: `${3 * u}px ${26 * u}px`,
                                        boxShadow: `0 ${1.5 * u}px ${3 * u}px rgba(0,0,0,0.25)`,
                                    }}
                                >
                                    <FitLine
                                        text={memo.proprietor}
                                        size={26 * u}
                                        style={{ fontWeight: 700, lineHeight: 1.3, textAlign: 'center' }}
                                    />
                                </div>
                            </div>
                        ) : null}

                        {on('distributor') && memo.distributor.trim() ? (
                            <FitLine
                                text={memo.distributor}
                                size={21 * u}
                                style={{ marginTop: 4 * u, fontWeight: 700, lineHeight: 1.3, color: S.ink }}
                            />
                        ) : null}

                        {on('tagline') && shop.tagline.trim() ? (
                            <div
                                style={{
                                    fontSize: 17 * u, fontWeight: 600, lineHeight: 1.4, color: S.note,
                                    margin: `${4 * u}px auto 0`, maxWidth: '96%',
                                }}
                            >
                                {shop.tagline}
                            </div>
                        ) : null}

                        {on('phone') && contacts ? (
                            <FitLine
                                text={contacts}
                                size={18 * u}
                                style={{ marginTop: 3 * u, fontWeight: 700, lineHeight: 1.35, color: S.ink }}
                            />
                        ) : null}
                    </div>
                </FitZone>
            </div>

            {/* ---- the maroon address band, edge to edge ---- */}
            {on('addressBand') ? (
                <div
                    style={{
                        flex: 'none', background: S.band, color: '#FFFFFF', textAlign: 'center',
                        padding: `${5 * u}px ${pad}px`,
                    }}
                >
                    <FitLine text={bandLine} size={23 * u} style={{ fontWeight: 700, lineHeight: 1.35 }} />
                </div>
            ) : null}

            {/* ---- the form ---- */}
            <div
                style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    padding: `${14 * u}px ${pad}px ${26 * u}px`, boxSizing: 'border-box',
                }}
            >
                {(memo.fields.memoNo || memo.fields.date) ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 * u, marginBottom: 10 * u }}>
                        <span style={{ fontSize: 21 * u, fontWeight: 700, lineHeight: 1.4, color: S.ink }}>
                            {memo.fields.memoNo ? `${words.memoNo}-` : ''}
                        </span>
                        {memo.fields.date ? (
                            <span style={{ display: 'flex', alignItems: 'stretch', height: 32 * u }}>
                                <span
                                    style={{
                                        display: 'flex', alignItems: 'center', background: S.tag, color: '#FFFFFF',
                                        padding: `0 ${10 * u}px`, fontSize: 20 * u, fontWeight: 700, lineHeight: 1.2,
                                        whiteSpace: 'nowrap', border: `${1.5 * u}px solid ${S.tag}`, boxSizing: 'border-box',
                                    }}
                                >
                                    <span>{labels.date}</span>
                                </span>
                                {[52, 52, 86].map((width, i) => (
                                    <span
                                        key={i}
                                        style={{
                                            display: 'block', width: width * u, background: '#FFFFFF',
                                            border: `${1.5 * u}px solid ${S.tag}`, borderLeft: 'none', boxSizing: 'border-box',
                                        }}
                                    />
                                ))}
                            </span>
                        ) : null}
                    </div>
                ) : null}

                {memo.fields.name ? <TagField label={labels.name} u={u} /> : null}
                {memo.fields.address ? (
                    <div style={{ marginTop: 7 * u }}>
                        <TagField label={labels.address} u={u} />
                    </div>
                ) : null}

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginTop: 9 * u }}>
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch' }}>
                        <ShopGrid memo={memo} u={u} />
                    </div>
                    <ShopTotals memo={memo} labels={labels} u={u} />

                    {/* The thank-you written faintly across the writing area. */}
                    {on('slogan') && memo.slogan.trim() ? (
                        <div
                            aria-hidden="true"
                            style={{
                                position: 'absolute', left: '10%', right: '28%', bottom: '8%',
                                textAlign: 'center', pointerEvents: 'none',
                                fontSize: 30 * u, fontWeight: 600, lineHeight: 1.2,
                                color: '#9AA06A', opacity: 0.85, whiteSpace: 'nowrap',
                                transform: 'skewX(-12deg) rotate(-3deg)',
                            }}
                        >
                            {memo.slogan}
                        </div>
                    ) : null}
                </div>

                {memo.showWords ? (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2 * u, marginTop: 14 * u }}>
                        <span style={{ fontSize: 20 * u, fontWeight: 700, lineHeight: 1.4, whiteSpace: 'nowrap', color: S.ink }}>
                            {`${words.inWords}:`}
                        </span>
                        <span style={{ flex: 1, borderBottom: `${2 * u}px dotted ${S.ink}`, height: 13 * u }} />
                    </div>
                ) : null}

                {memo.footerNote ? (
                    <div style={{ marginTop: 8 * u, fontSize: 16 * u, fontWeight: 600, lineHeight: 1.4, color: S.note }}>
                        {memo.footerNote}
                    </div>
                ) : null}

                {/* The two signatures are written straight on the paper here —
                    there is no strip under them on this pad. */}
                {memo.showSignature ? (
                    <div
                        style={{
                            marginTop: 54 * u, display: 'flex', alignItems: 'flex-end',
                            justifyContent: 'space-between', gap: 30 * u,
                            fontSize: 20 * u, fontWeight: 700, lineHeight: 1.35, color: S.ink,
                        }}
                    >
                        <span>{`${labels.customerSign}-`}</span>
                        <span>{`${labels.sellerSign}-`}</span>
                    </div>
                ) : null}
            </div>
        </>
    );
}
